<script>
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-stores
  import { page } from "$app/stores";
  import { beforeUpdate, tick } from "svelte";
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-browser
  import { browser } from "$app/environment";

  import { ITEM_PREVIEW_LIST_FORMS, BREAK_POINTS } from "$lib/utilities/constants";
  import { textToId } from "$lib/utilities/dom-helpers";
  import {
    getItemsFormFactor,
    getItemComponent,
    createListProps,
    createItemProps,
    getItemsPerPage,
    createItemContainerClass
  } from "$lib/utilities";

  import Bleed from "$lib/components/Bleed.svelte";
  import Grid from "$lib/components/Grid.svelte";
  import Markdown from "$lib/components/Markdown.svelte";
  import Pagination, { paginateItems } from "$lib/components/Pagination/Pagination.svelte";
  import PageSectionHeader from "$lib/components/Page/PageSectionHeader.svelte";
  import PlaceholderCard from "$lib/components/Card/Placeholder.svelte";
  import SeeMoreButton from "$lib/components/SeeMoreButton.svelte";

  export let data;
  export let listContainerClass = "";
  export let itemsPerPage = 36;
  export let hideSectionHeader = false;
  export let containerRef;

  let windowWidth;

  const {
    HORIZONTAL_FULL_WIDTH,
    HORIZONTAL_FULL_WIDTH_CONTAINED,
    VERTICAL_CARD_2UP,
    VERTICAL_CARD_3UP,
    VERTICAL_CARD_4UP,
    VERTICAL_CARD_2UP_CONTAINED,
    VERTICAL_CARD_3UP_CONTAINED,
    VERTICAL_CARD_4UP_CONTAINED
  } = ITEM_PREVIEW_LIST_FORMS;

  /**
   * Determine if a vertical card in the IPL will convert to horizontal card IPL
   * based on windowWidth and number of rows
   * @param {number} windowWidth - the px width of the window
   * @returns {boolean} True/False whether the conversion will happen
   */
  function verticalToHorizontalConditions(windowWidth) {
    let verticalCardsNeedToConvert = false;
    switch (data.fields.itemsFormFactor) {
      case VERTICAL_CARD_2UP:
        verticalCardsNeedToConvert = data.children.length / 2 > 2;
        break;
      case VERTICAL_CARD_3UP:
        verticalCardsNeedToConvert = data.children.length / 3 > 2;
        break;
      case VERTICAL_CARD_4UP:
        verticalCardsNeedToConvert = data.children.length / 4 > 2;
        break;
    }

    return verticalCardsNeedToConvert && windowWidth <= BREAK_POINTS.sm;
  }

  function verticalContainedToHorizontalContainedConditions(windowWidth) {
    let verticalCardsNeedToConvert = false;
    switch (data.fields.itemsFormFactor) {
      case VERTICAL_CARD_2UP_CONTAINED:
        verticalCardsNeedToConvert = data.children.length / 2 > 2;
        break;
      case VERTICAL_CARD_3UP_CONTAINED:
        verticalCardsNeedToConvert = data.children.length / 3 > 2;
        break;
      case VERTICAL_CARD_4UP_CONTAINED:
        verticalCardsNeedToConvert = data.children.length / 4 > 2;
        break;
    }

    return verticalCardsNeedToConvert && windowWidth <= BREAK_POINTS.sm;
  }

  $: overrides = [
    {
      conditionsMet: verticalToHorizontalConditions(windowWidth),
      newItemsFormFactor: HORIZONTAL_FULL_WIDTH
    },
    {
      conditionsMet: verticalContainedToHorizontalContainedConditions(windowWidth),
      newItemsFormFactor: HORIZONTAL_FULL_WIDTH_CONTAINED
    }
  ];
  $: itemsFormFactor = getItemsFormFactor(data, overrides);

  let activePaginatedItems = [];
  let activePage;
  $: currentUrl = $page.url;

  // vars for IPLs using See More / See Less button instead of pagination (introduced during Dem Forum 2023)
  let hasSeeMoreButton = !!data.fields.seeMoreButton;
  let seeMoreItems = [];
  $: seeMorePaginatedItems = hasSeeMoreButton ? [data.children.slice(0, 4), data.children] : [];

  beforeUpdate(async () => {
    // wait for all state updates to complete
    await tick();
    setActivePage();
  });

  if (browser) {
    // going back and forth in history doesn't actually trigger a page load in our case;
    // use callback instead
    window.onpopstate = setActivePage;
  }

  function setActivePage() {
    // get data from session history (when paginating) or search params (when navigating)
    const ipl = history.state?.ipl || currentUrl?.searchParams.get("ipl");

    if (ipl && ipl === data.sys?.id) {
      const page = history.state?.page || parseInt(currentUrl?.searchParams.get("page"));
      activePage = !isNaN(page) ? page : 1;
    } else {
      activePage = 1;
    }
  }

  async function onNavigate(event) {
    const newUrl = new URL(currentUrl.href);
    const page = event.detail.page;

    if (!history.state?.page && !currentUrl?.searchParams.get("page")) {
      // rewrite first page session history
      newUrl.searchParams.set("ipl", data.sys?.id);
      newUrl.searchParams.set("page", 1);
      history.replaceState(
        {
          ipl: data.sys?.id,
          page: 1
        },
        "",
        newUrl
      );
    }

    newUrl.searchParams.set("ipl", data.sys?.id);
    newUrl.searchParams.set("page", page);
    history.pushState(
      {
        ipl: data.sys?.id,
        page
      },
      "",
      newUrl
    );

    setActivePage();

    if (!hasSeeMoreButton) {
      setTimeout(() => {
        window.scrollTo({
          top: containerRef?.offsetTop || 0,
          behavior: "smooth"
        });
      }, 10);
    }
  }

  $: ({ numOfPages, paginatedItems } = paginateItems(
    data.children,
    getItemsPerPage(itemsFormFactor, itemsPerPage)
  ));
  $: {
    activePaginatedItems = paginatedItems?.[activePage - 1] ?? [];
  }
  $: {
    seeMoreItems = seeMorePaginatedItems?.[activePage - 1] ?? [];
  }

  $: activeItems = hasSeeMoreButton ? seeMoreItems : activePaginatedItems;
</script>

<svelte:window bind:innerWidth={windowWidth} />

<div class="embedded-content" use:setActivePage>
  {#if data.fields.headingText}
    <div class="pb-48 lg:pb-72" class:sr-only={hideSectionHeader} data-headings-increment>
      <PageSectionHeader eyebrow={data.fields.eyebrow} headline={data.fields.headingText}>
        <Markdown input={data.fields.eyebrow} />
      </PageSectionHeader>
    </div>
  {/if}

  <Bleed fullWidth>
    <Grid
      containerElement="ul"
      class="list-none gap-48 lg:gap-72 lg:max-w-screen-lg mx-auto px-24 lg:px-72 {listContainerClass}"
      data-testid="itemPreviewList"
      aria-labelledby={textToId(data.fields.headingText || "")}
      bind:ref={containerRef}
      center
      {...createListProps(itemsFormFactor)}
    >
      {#if activePage && itemsFormFactor}
        {#each activeItems as item}
          <li class="w-full {createItemContainerClass(itemsFormFactor)}">
            <svelte:component
              this={getItemComponent(itemsFormFactor)}
              {...createItemProps(item, itemsFormFactor)}
            />
          </li>
        {/each}
      {:else}
        {@const numOfItemsToMock =
          data.children.length > itemsPerPage ? itemsPerPage : data.children.length}
        <!-- eslint-disable-next-line no-unused-vars -->
        {#each new Array(numOfItemsToMock).fill(null) as _}
          <li class="w-full">
            <PlaceholderCard />
          </li>
        {/each}
      {/if}
    </Grid>
  </Bleed>
  <Bleed fullWidth data-headings-increment>
    {#if !hasSeeMoreButton}
      <Pagination
        numOfItems={data.children?.length}
        {numOfPages}
        itemsPerPage={getItemsPerPage(itemsFormFactor, itemsPerPage)}
        initialActivePage={activePage}
        on:navigate={onNavigate}
        class="w-auto mx-auto py-48"
      />
    {/if}
    {#if hasSeeMoreButton && data.children.length > 4}
      <SeeMoreButton
        numOfItems={seeMoreItems.length}
        collapsedButtonText={data.fields.collapsedButtonText}
        expandedButtonText={data.fields.expandedButtonText}
        on:navigate={onNavigate}
        identifier={data?.sys?.id}
      />
    {/if}
  </Bleed>
</div>
